import { useState } from "react";
import { styled } from "@glitz/react";
import { fontSize, spacing, Theme, red, darkGrey } from "../theme";
import Input from "./basic/TextInput";
import Button from "./basic/Button";
import DeliveryOptionsWidget from "./DeliveryOptionsWidget";
import {
  isDeliverySelectionComplete,
  useShipping,
} from "../context/ShippingContext";

type Field =
  | "firstName"
  | "lastName"
  | "address"
  | "city"
  | "phone"
  | "email"
  | "zipCode";

type Errors = Partial<Record<Field, string>>;

const normalizeSwedishZip = (value: string) => value.replace(/\s+/g, "").trim();
const isValidSwedishZip = (value: string) =>
  /^\d{5}$/.test(normalizeSwedishZip(value));
const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const normalizePhone = (value: string) => value.replace(/[^\d+]/g, "").trim();
const isValidPhone = (value: string) => {
  const v = normalizePhone(value);
  if (!v || !/^\+?\d+$/.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

type Props = {
  onContinue: () => void;
};

/**
 * Kassans fraktsteg (TMA):
 * 1) Postnummer → leveransalternativ (+ ombud/box vid behov)
 * 2) Kontakt (namn, e-post, telefon)
 * 3) Gatuadress + ort endast vid hemleverans
 */
const TmaCheckoutShippingStep = ({ onContinue }: Props) => {
  const { shipping, deliverySelection, setShipping, updateShippingField } =
    useShipping();

  const [zipCode, setZipCode] = useState(shipping.postcode);
  const [firstName, setFirstName] = useState(shipping.first_name);
  const [lastName, setLastName] = useState(shipping.last_name);
  const [address, setAddress] = useState(shipping.address_1);
  const [city, setCity] = useState(shipping.city);
  const [phone, setPhone] = useState(shipping.phone);
  const [email, setEmail] = useState(shipping.email);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState("");

  const postalCode = normalizeSwedishZip(zipCode);
  const zipOk = isValidSwedishZip(postalCode);
  const deliveryOk = isDeliverySelectionComplete(deliverySelection);
  // Hemadress bara vid hemleverans — inte ombud/box/butik
  const needsHomeAddress = deliverySelection?.type === "home";
  const isStorePickup = deliverySelection?.type === "store_pickup";
  const isServicePointPickup = Boolean(deliverySelection?.requiresServicePoint);

  const clearFieldError = (field: Field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (formError) setFormError("");
  };

  const onZipChange = (value: string) => {
    setZipCode(value);
    clearFieldError("zipCode");
    const normalized = normalizeSwedishZip(value);
    if (/^\d{5}$/.test(normalized)) {
      updateShippingField("postcode", normalized);
    }
  };

  const validate = (): boolean => {
    const next: Errors = {};

    if (!zipOk) {
      next.zipCode = "Ange ett giltigt postnummer (5 siffror)";
    }
    if (!deliveryOk) {
      setFormError(
        deliverySelection?.requiresServicePoint
          ? "Välj leveranssätt och utlämningsställe."
          : "Välj leveranssätt."
      );
      setErrors(next);
      return false;
    }

    if (!firstName.trim()) next.firstName = "Ange förnamn";
    if (!lastName.trim()) next.lastName = "Ange efternamn";

    if (!email.trim()) next.email = "Ange e-post";
    else if (!isValidEmail(email)) next.email = "Ange en giltig e-postadress";

    if (!phone.trim()) next.phone = "Ange telefonnummer";
    else if (!isValidPhone(phone))
      next.phone = "Ange ett giltigt telefonnummer";

    if (needsHomeAddress) {
      if (!address.trim()) next.address = "Ange gatuadress";
      if (!city.trim()) next.city = "Ange ort";
    }

    setErrors(next);
    const ok = Object.keys(next).length === 0;
    setFormError(ok ? "" : "Kontrollera fälten markerade i formuläret.");
    return ok;
  };

  const onClickNext = () => {
    if (!validate() || !deliverySelection) return;

    const normalizedZip = normalizeSwedishZip(zipCode);

    setShipping({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      address_1: isServicePointPickup
        ? deliverySelection.servicePointStreet ||
          deliverySelection.servicePointName ||
          deliverySelection.title ||
          "Utlämningsställe"
        : isStorePickup
          ? deliverySelection.title || "Hämta i butik"
          : address.trim(),
      address_2: isServicePointPickup
        ? deliverySelection.servicePointName || ""
        : isStorePickup
          ? "Butikshämtning"
          : "",
      postcode: isServicePointPickup
        ? deliverySelection.servicePointPostalCode || normalizedZip
        : normalizedZip,
      city: isServicePointPickup
        ? deliverySelection.servicePointCity || city.trim() || "—"
        : isStorePickup
          ? city.trim() || "—"
          : city.trim(),
      state: "",
      phone: normalizePhone(phone),
      email: email.trim(),
    });

    onContinue();
  };

  return (
    <Wrapper>
      <Header>Leverans</Header>

      <Section>
        <SectionTitle>1. Postnummer</SectionTitle>
        <Input
          label="Postnummer"
          value={zipCode}
          onChange={(e) => onZipChange(e.target.value)}
          error={Boolean(errors.zipCode)}
          helpText={errors.zipCode}
          placeholder="t.ex. 111 22"
        />
        <Hint>
          Vi visar fraktalternativ utifrån postnummer. Full adress behövs bara
          vid hemleverans.
        </Hint>
      </Section>

      {zipOk ? (
        <Section>
          <SectionTitle>2. Leveranssätt</SectionTitle>
          <DeliveryOptionsWidget postalCode={postalCode} city={city || undefined} />
        </Section>
      ) : null}

      {deliveryOk ? (
        <Section>
          <SectionTitle>3. Dina uppgifter</SectionTitle>
          <FormGrid>
            <Input
              label="Förnamn"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                clearFieldError("firstName");
              }}
              error={Boolean(errors.firstName)}
              helpText={errors.firstName}
            />
            <Input
              label="Efternamn"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                clearFieldError("lastName");
              }}
              error={Boolean(errors.lastName)}
              helpText={errors.lastName}
            />
            <Input
              label="E-post"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError("email");
              }}
              error={Boolean(errors.email)}
              helpText={errors.email}
            />
            <Input
              label="Telefonnummer"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                clearFieldError("phone");
              }}
              error={Boolean(errors.phone)}
              helpText={errors.phone}
            />

            {needsHomeAddress ? (
              <>
                <Input
                  label="Gatuadress"
                  placeholder="Gata och husnummer"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    clearFieldError("address");
                  }}
                  error={Boolean(errors.address)}
                  helpText={errors.address}
                />
                <Input
                  label="Ort"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    clearFieldError("city");
                  }}
                  error={Boolean(errors.city)}
                  helpText={errors.city}
                />
              </>
            ) : (
              <PickupNote>
                {deliverySelection?.type === "store_pickup" ? (
                  <>
                    Du hämtar i butik (
                    <strong>{deliverySelection.title || "butik"}</strong>
                    ). Vi behöver ingen hemadress.
                  </>
                ) : (
                  <>
                    Paketet skickas till{" "}
                    <strong>
                      {deliverySelection?.servicePointName || "valt ställe"}
                    </strong>
                    . Vi behöver ingen hemadress.
                  </>
                )}
              </PickupNote>
            )}
          </FormGrid>
        </Section>
      ) : null}

      {formError ? <FormErrorText>{formError}</FormErrorText> : null}

      <Button as={Theme.Dark} onClick={onClickNext}>
        Nästa steg
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "50%",
  justifySelf: "center",
  gap: spacing.l,
  marginBottom: spacing.xxl,
});

const Header = styled.h1({
  textAlign: "center",
  margin: { bottom: 0 },
});

const Section = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
});

const SectionTitle = styled.h2({
  fontSize: fontSize.m,
  margin: { xy: 0 },
});

const Hint = styled.p({
  margin: { xy: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
});

const FormGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: spacing.m,
  width: "100%",
});

const PickupNote = styled.div({
  fontSize: fontSize.s,
  color: darkGrey,
  padding: { xy: spacing.s },
  backgroundColor: "#f7f7f7",
  borderRadius: "8px",
});

const FormErrorText = styled.div({
  fontSize: fontSize.s,
  color: red,
  fontWeight: 500,
  textAlign: "center",
});

export default TmaCheckoutShippingStep;
